import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "../../scss/registration/registration.scss";
import "../../scss/registration/auth-elevate.scss";
import { PillTabs } from "../../components/pill-tabs/pill-tabs.component";
import { PasswordStrength } from "../../components/password-strength/password-strength.component";
import { FieldRules } from "../../components/field-rules/field-rules.component";
import { SelectField } from "../../components/select-field/select-field.component";
import { SignupGuide } from "../../components/signup-guide/signup-guide.component";
import { AuthHeroTitle } from "../../components/auth-hero-title.component";
import type { FieldRule } from "../../components/field-rules/field-rules.component";
import {
    UserFieldIcon,
    MailFieldIcon,
    LockFieldIcon,
    IdFieldIcon,
    CalendarFieldIcon,
    GlobeFieldIcon,
    PhoneFieldIcon,
    ProfessionFieldIcon,
    AddressFieldIcon,
    PostalFieldIcon,
    CityFieldIcon,
    CtaArrowIcon,
} from "../../components/auth-field-icons";
import {
    validateForm,
    handleSSNInput,
    handlePhoneInput,
    handleDOBInput,
    formatDOBDisplay,
    validateDOB,
    dobDaysInMonth,
    validateEmail,
    validatePassword,
    validateLatinCharacters,
    validatePostalCode,
    validatePhoneNumber,
    validateSsn,
    isGuestInvozioEmail,
} from '../../utils/registration-validation';
import { FreelancerIcon, BusinessIcon, Calendar, ShieldIcon, bankidcolor } from "../../components/images/svg/icons"
import BankIDSignupInline from '../../components/BankIDSignupInline/BankIDSignupInline';
import { Col, Container, Row } from "react-bootstrap";
import { UIInput } from "../../components/ui-input";
import { socialLogins } from "../../constant/sign-up.constant";
import { LOCALES } from "../../../common/constants";
import { Link, useNavigate } from "react-router-dom";
import { getItem, getLocalizedPathFromLocalStorage, setItem } from "../../helper/localstorage.helper";
import { CalendarSingle } from '../../components/calendar';

import { useDispatch, useSelector } from "react-redux";
import { prepareRegistrationPayload } from "../../helper/signup.helpers";
import { registerUser } from "../../features/signup/signup.slice";
import { blobURL } from "../../../common/StringUtils";
import { signInWithEmailAndPassword } from "../../features/signin/signin.slice";
import { RegistrationHeader } from "../../components/registeration-header-.component";
import useOutsideClick from '../../hooks/useOutsideClick';
import useIsWideScreen from '../../hooks/useIsWideScreen';
import useToastRelay from '../../hooks/useToastRelay';
import { getCountries } from "../../utils/countries";
import CryptoJS from "crypto-js";
import { INVOZIO_BLOB_BASE } from "../../constant/video-url-import.constant";

interface RegistrationFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    zipCode: string;
    country: string;
    address: string;
    city: string;
    profession: string;
    socialSecurityNumber: string;
    dateOfBirth: string;
    agreeToTerms: boolean;
    noIdNumber: boolean;
    phoneNo?: string;
    accountType: 'freelancer' | 'business';
    ReferredByUserId?: string;
    ReferredByCode?: string;
}

interface FormErrors {
    [key: string]: string;
}

/* ── Date helpers for the live DOB rules (pure, no Date parsing) ────────── */
const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInMonth = (year: number, month: number) =>
    [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;

/** Today as a comparable YYYYMMDD number, e.g. 20260806. */
const todayStamp = () => {
    const now = new Date();
    return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

export const Registration: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch() as any;
    const { loading: isSubmitting, error: submitError } = useSelector((state: any) => state.signup);
    const [accountType, setAccountType] = useState<'freelancer' | 'business'>('freelancer');
    const [loading, setLoading] = useState(false);
    const [signupMode, setSignupMode] = useState<'bankid-active' | 'email'>('bankid-active');
    const [showDOBCalendar, setShowDOBCalendar] = useState(false);
    /* Wizard: the form is split into three steps so it never looks endless.
       Presentation only — submitting still runs the same validateForm(). */
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [activeField, setActiveField] = useState<string | null>(null);
    /* Why the last Next was refused — the guide reads this out. */
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    /* Submitting is only armed once step 4 has actually been painted. */
    const [submitArmed, setSubmitArmed] = useState(false);
    /* Something the character should announce (success or failure). */
    const [announcement, setAnnouncement] = useState<{ message: string; kind: 'error' | 'success' } | null>(null);
    /* On phones the character is small, so those keep the toast. */
    const isWideScreen = useIsWideScreen();

    /* An announcement holds the cloud, so it needs a way out. The short grace
       period stops a message being wiped by the same render that produced it —
       a BankID error often arrives together with the view changing under it. */
    const announcedAt = useRef(0);

    const announce = useCallback((message: string, kind: 'error' | 'success') => {
        announcedAt.current = Date.now();
        setAnnouncement({ message, kind });
    }, []);

    const dismissAnnouncement = useCallback(() => {
        if (Date.now() - announcedAt.current < 600) return;
        setAnnouncement(null);
        setBlockedReason(null);
    }, []);


    /* One place decides where a message goes: the character on big screens,
       a toast on phones. Everything below calls this instead of toast.*. */
    const notify = useCallback((message: string, kind: 'error' | 'success' = 'error') => {
        if (isWideScreen) {
            announce(message, kind);
            return;
        }
        (kind === 'success' ? toast.success : toast.error)(message);
    }, [isWideScreen, announce]);

    /* Toasts raised from shared BankID code (its slice, component and hook)
       are relayed to the character too — those files stay untouched so the
       invoice and ID-verification flows keep their toasts. */
    const relayToGuide = useCallback((message: string, kind: 'error' | 'success') => {
        announce(message, kind);
    }, [announce]);

    const rawToast = useToastRelay(isWideScreen, relayToGuide);

    /* Pressing Sign Up is the one moment that also raises a real toast. Every
       other message stays with the character, but this one follows a deliberate
       action whose outcome the user is waiting on — and the character sits on
       the other side of the screen from the button they just clicked. On narrow
       screens notify() already toasts, so only wide screens add one here. */
    const notifySubmit = useCallback((message: string, kind: 'error' | 'success' = 'error') => {
        notify(message, kind);
        if (isWideScreen) (kind === 'success' ? rawToast.success : rawToast.error)(message);
    }, [notify, isWideScreen, rawToast]);

    const currentLanguage = new URLSearchParams(location.search).get('lang') || 'sv-se';
    const invoicePDF = `${blobURL}/invoice.pdf`;
    const invoiceSwidishPDF = `${blobURL}/invoiceSwidish.pdf`;
    /* The general conditions are a different agreement for each account type,
       and each exists in both languages — the file name carries both, so one
       expression covers the four combinations. It follows the account-type
       tabs live: switching to Business on step 1 changes the link on step 4. */
    const generalConditionsUrl = useMemo(() => {
        const plan = accountType === 'business' ? 'Business' : 'Freelancer';
        const lang = currentLanguage === LOCALES.EN_US ? 'En' : 'Se';
        return `${INVOZIO_BLOB_BASE}/Invozio_${plan}_Agreement_Terms_${lang}.pdf`;
    }, [accountType, currentLanguage]);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const dropdownRef = useOutsideClick(() => setShowDOBCalendar(false)) as React.MutableRefObject<HTMLDivElement | null>;
    const params = new URLSearchParams(window.location.search);
    const referredByCode = params.get('inviteCode') || params.get('invitecode') || '';
    const referredByUserId = params.get('userId') || '';
    const isFreelancerInvitated = params.get('isFreelancerInvitated') === 'True';

    if (referredByCode) setItem('inviteCode', referredByCode);
    if (referredByUserId) setItem('userId', referredByUserId);
    const tduid = getItem('tduid') ?? null

    const [formData, setFormData] = useState<RegistrationFormData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        socialSecurityNumber: '',
        country: '',
        dateOfBirth: '',
        agreeToTerms: false,
        noIdNumber: false,
        zipCode: '',
        address: '',
        city: '',
        profession: '',
        phoneNo: '',
        accountType: 'freelancer',
        ReferredByCode: referredByCode,
        ReferredByUserId: referredByUserId,
    });

    const [errors, setErrors] = useState<FormErrors>({});

    /* Same values the native <select> submitted (country.name), same translated
       labels — Sweden and USA stay on top, just without appearing twice. */
    const countryOptions = useMemo(() => {
        const pinnedNames = ['Sweden', 'USA'];
        const all = getCountries();
        /* The value stays the English name the API expects; only the label is
           translated — the pinned pair used to skip that and stayed English
           while the rest of the list switched language. */
        const toOption = (country: { name: string; translationKey: string }, pinned = false) => ({
            value: country.name,
            label: (t(country.translationKey) as string) || country.name,
            ...(pinned ? { pinned: true } : {}),
        });

        return [
            ...pinnedNames
                .map((name) => all.find((country) => country.name === name))
                .filter((country): country is NonNullable<typeof country> => !!country)
                .map((country) => toOption(country, true)),
            ...all
                .filter((country) => !pinnedNames.includes(country.name))
                .map((country) => toOption(country)),
        ];
    }, [t]);

    /* Descriptions are the product's own wording for the two account types,
       reused from the plan pages rather than written fresh here — the step
       should tell the user what each one means, not invent claims. */
    const accountTypes = [
        {
            key: 'freelancer',
            icon: FreelancerIcon,
            label: t("REGISTRATION_FREELANCER"),
            description: t("REGISTRATION_FREELANCER_DESCRIPTION"),
        },
        {
            key: 'business',
            icon: BusinessIcon,
            label: t("REGISTRATION_BUSINESS"),
            description: t("REGISTRATION_BUSINESS_DESCRIPTION"),
        },
    ];

    /* ── Presentation-only derived state ─────────────────────────────────
       Per-field "looks good" flags for the inline tick, and a completion
       percentage for the progress meter. Both are computed from formData —
       no extra state, and the submit-time validation is untouched. */
    /* ── Live field rules ────────────────────────────────────────────────
       Each rule is pending until the value is complete enough to judge, then
       it resolves to valid or invalid. They mirror the existing validators —
       nothing here changes what submitting accepts. */

    /* SSN — the same rules validateSsn() applies at submit time, shown one per line.
       Each rule stays pending until all 12 digits are in, because none of the date
       parts can be judged from a half-typed number. */
    const ssnValue = formData.socialSecurityNumber;
    const ssnComplete = ssnValue.length === 12;
    const ssnError = ssnComplete ? validateSsn(ssnValue) : '';

    /* Listed in the order validateSsn() checks them, so the error it reports names the
       first rule that failed: everything before it passed, and everything after it could
       not be judged yet and stays pending rather than claiming to be satisfied. */
    const SSN_RULES: { key: string; errors: string[] }[] = [
        { key: 'SSN_RULE_12_DIGITS', errors: ['SSN_NUMBER_MUST_HAVE_12_DIGITS'] },
        { key: 'SSN_RULE_LAST_FOUR', errors: ['SSN_LAST_4_DIGITS_CANNOT_BE_ALL_ZEROS'] },
        {
            key: 'SSN_RULE_VALID_DATE',
            errors: ['SSN_YEAR_INVALID', 'SSN_MONTH_INVALID', 'SSN_DAY_INVALID', 'SSN_DATE_INVALID'],
        },
        { key: 'SSN_RULE_MIN_AGE', errors: ['SSN_MIN_AGE'] },
    ];

    const ssnFailedIndex = ssnError
        ? SSN_RULES.findIndex((rule) => rule.errors.includes(ssnError))
        : -1;

    const ssnRules: FieldRule[] = SSN_RULES.map((rule, index) => ({
        key: rule.key,
        status: !ssnComplete
            ? 'pending'
            : ssnFailedIndex === -1 || index < ssnFailedIndex
                ? 'valid'
                : index === ssnFailedIndex
                    ? 'invalid'
                    : 'pending',
    }));

    /* DOB — handleDOBInput stores YYYYMMDD + '000', so only the first eight
       digits carry the date. Judged with plain integer maths (no Date parsing,
       so no timezone/locale surprises), and each rule stands on its own: the
       future check must not depend on the calendar check, otherwise one bad
       result leaves the other stuck on "pending" forever. */
    const dobDate = formData.dateOfBirth.replace(/\D/g, '').slice(0, 8);
    const dobComplete = dobDate.length === 8;

    const dobYear = Number(dobDate.slice(0, 4));
    const dobMonth = Number(dobDate.slice(4, 6));
    const dobDay = Number(dobDate.slice(6, 8));

    const dobIsCalendarDate =
        dobComplete &&
        dobYear >= 1900 &&
        dobMonth >= 1 &&
        dobMonth <= 12 &&
        dobDay >= 1 &&
        dobDay <= daysInMonth(dobYear, dobMonth);

    const dobNotFuture = dobComplete && Number(dobDate) <= todayStamp();

    const dobRules: FieldRule[] = [
        {
            key: 'DOB_RULE_8_DIGITS',
            status: dobComplete ? 'valid' : 'pending',
        },
        {
            key: 'DOB_RULE_VALID_DATE',
            status: !dobComplete ? 'pending' : dobIsCalendarDate ? 'valid' : 'invalid',
            advisory: true,
        },
        {
            key: 'DOB_RULE_NOT_FUTURE',
            status: !dobComplete ? 'pending' : dobNotFuture ? 'valid' : 'invalid',
            advisory: true,
        },
    ];

    const isFieldValid = {
        firstName: !!formData.firstName.trim() && !errors.firstName,
        lastName: !!formData.lastName.trim() && !errors.lastName,
        email: !!formData.email.trim() && validateEmail(formData.email.trim()) && !errors.email,
        password: !!formData.password && validatePassword(formData.password) && !errors.password,
        socialSecurityNumber:
            ssnRules.every((rule) => rule.status === 'valid') && !errors.socialSecurityNumber,
        dateOfBirth:
            dobRules.every((rule) => rule.status === 'valid') && !errors.dateOfBirth,
        phoneNo: !!formData.phoneNo?.trim() && validatePhoneNumber(formData.phoneNo) && !errors.phoneNo,
        address: !!formData.address.trim() && !errors.address,
        zipCode: !!formData.zipCode.trim() && validatePostalCode(formData.zipCode.trim()) && !errors.zipCode,
        city: !!formData.city.trim() && !errors.city,
        profession: !!formData.profession.trim() && !errors.profession,
        country: !!formData.country,
    };


    /* Guide context — everything the character needs to comment on the field
       the user is in. Derived, never stored. */

    /* The BankID option buttons come from the shared BankIDComponent, so the
       guide listens on a wrapper rather than that component being modified. */
    const bankidHoverProps = {
        onMouseOver: (e: React.MouseEvent) => {
            const option = (e.target as HTMLElement).closest?.('.bankid-option');
            if (!option) {
                setActiveField(null);
                return;
            }
            const index = Array.from(option.parentElement?.children || []).indexOf(option);
            setActiveField(index === 0 ? 'bankidDevice' : 'bankidQr');
        },
        onMouseLeave: () => setActiveField(null),
    };


    /* Which BankID stage is on screen. Read from the DOM the shared component
       renders (`.bankid-options` → choosing, `.bankid-inline-qr` → QR shown),
       so that component keeps working untouched for the other flows. */
    const bankidWrapRef = useRef<HTMLDivElement | null>(null);
    const [bankidStage, setBankidStage] = useState<'qr' | 'waiting' | null>(null);

    useEffect(() => {
        const el = bankidWrapRef.current;
        if (!el) {
            setBankidStage(null);
            return;
        }

        const update = () => {
            if (el.querySelector('.bankid-inline-qr, .bankid-qr-container')) setBankidStage('qr');
            else if (!el.querySelector('.bankid-options')) setBankidStage('waiting');
            else setBankidStage(null);
        };

        update();
        const observer = new MutationObserver(update);
        observer.observe(el, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [signupMode]);


    /* anything the guide can talk about (a hovered link, a focused field) or
       leaving the BankID stage dismisses whatever was announced */
    useEffect(() => {
        if (activeField) dismissAnnouncement();
    }, [activeField, dismissAnnouncement]);

    useEffect(() => {
        dismissAnnouncement();
    }, [bankidStage, dismissAnnouncement]);

    const activeFieldError = activeField ? errors[activeField] : '';

    const guideContext = {
        step,
        activeField: bankidStage === 'qr'
            ? 'bankidQrOpen'
            : bankidStage === 'waiting'
                ? 'bankidWaiting'
                : activeField,
        alert: announcement
            ? { message: announcement.message, kind: announcement.kind, at: announcedAt.current }
            : blockedReason
            ? { message: t(blockedReason) as string, blocking: true }
            : activeFieldError
                ? { message: activeFieldError }
                : null,
        values: {
            email: formData.email,
            password: formData.password,
            socialSecurityNumber: formData.socialSecurityNumber,
            dateOfBirth: formData.dateOfBirth,
            phoneNo: formData.phoneNo,
            zipCode: formData.zipCode,
        },
        valid: {
            ...(isFieldValid as unknown as Record<string, boolean>),
            /* everything the final submit still checks on this step */
            readyToSubmit:
                isFieldValid.address && isFieldValid.country && formData.agreeToTerms,
        },
        flags: {
            ssnZeros: ssnError === 'SSN_LAST_4_DIGITS_CANNOT_BE_ALL_ZEROS',
            ssnBad: !!ssnError,
            dobBadDate: dobComplete && !dobIsCalendarDate,
            phoneBad: !!formData.phoneNo?.trim() && !validatePhoneNumber(formData.phoneNo),
            postalBad: !!formData.zipCode.trim() && !validatePostalCode(formData.zipCode.trim()),
            emailBad: !!formData.email.trim() && !validateEmail(formData.email.trim()),
            passwordBad: !!formData.password && !validatePassword(formData.password),
        },
    };

    /* ── Wizard navigation ───────────────────────────────────────────────
       Each step gates on exactly the rules validateForm() applies to those
       fields, so anything that passes a step also passes submitting. */
    const stepFieldErrors = (target: 1 | 2 | 3 | 4): FormErrors => {
        const stepErrors: FormErrors = {};

        /* step 1 is the account-type choice — one of the two is always
           selected, so there is nothing that can be missing there. */

        if (target === 2) {
            if (!formData.firstName.trim()) stepErrors.firstName = "FULL_NAME_REQUIRED";
            else if (!validateLatinCharacters(formData.firstName)) stepErrors.firstName = "FIRST_NAME_LATIN_ONLY";

            if (!formData.lastName.trim()) stepErrors.lastName = "LAST_NAME_REQUIRED";
            else if (!validateLatinCharacters(formData.lastName)) stepErrors.lastName = "LAST_NAME_LATIN_ONLY";

            if (!formData.email.trim()) stepErrors.email = "EMAIL_REQUIRED";
            else if (!validateEmail(formData.email)) stepErrors.email = "EMAIL_INVALID";
            else if (isGuestInvozioEmail(formData.email)) stepErrors.email = "GUEST_EMAIL_NOT_ALLOWED";

            if (!formData.password) stepErrors.password = "PASSWORD_REQUIRED";
            else if (!validatePassword(formData.password)) stepErrors.password = "PASSWORD_RULE";
        }

        if (target === 3) {
            if (!formData.noIdNumber) {
                const stepSsnError = validateSsn(formData.socialSecurityNumber);
                if (stepSsnError) stepErrors.socialSecurityNumber = stepSsnError;
            }

            if (formData.noIdNumber && formData.dateOfBirth.length !== 11) {
                stepErrors.dateOfBirth = "DOB_MUST_HAVE_8_DIGITS.";
            }

            if (!formData.phoneNo) stepErrors.phoneNo = "INVALID_PHONE_NUMBER";
        }

        return stepErrors;
    };

    /* Swipe hints for the card (its scrollbar is hidden): "down" only while
       more is below, "up" only once scrolled, neither when everything fits.
       Same rules and thresholds as the partner modal's hints, so the two
       behave identically. */
    const cardRef = useRef<HTMLDivElement | null>(null);
    const [scrollNav, setScrollNav] = useState({ up: false, down: false });

    useEffect(() => {
        const el = cardRef.current;
        if (!el || signupMode !== 'email') return;

        const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const maxScroll = scrollHeight - clientHeight;
            setScrollNav({
                up: scrollTop > 8,
                down: maxScroll > 8 && scrollTop < maxScroll - 8,
            });
        };

        update();
        el.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        const ro = new ResizeObserver(update);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);

        return () => {
            el.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            ro.disconnect();
        };
    }, [signupMode, step]);

    /* Clicking a hint moves the card by ~85% of what is on screen — the
       partner modal's step, so a click always leaves some overlap. */
    const scrollCardByHint = (direction: 1 | -1) => {
        const el = cardRef.current;
        if (!el) return;
        el.scrollBy({ top: direction * el.clientHeight * 0.85, behavior: 'smooth' });
    };

    useEffect(() => {
        if (step !== 4) {
            setSubmitArmed(false);
            return;
        }
        const frame = requestAnimationFrame(() => setSubmitArmed(true));
        return () => cancelAnimationFrame(frame);
    }, [step]);

    const steps = [
        { id: 1 as const, label: t("SIGNUP_SECTION_ACCOUNT_TYPE") },
        { id: 2 as const, label: t("SIGNUP_SECTION_ACCOUNT") },
        { id: 3 as const, label: t("SIGNUP_SECTION_PERSONAL") },
        { id: 4 as const, label: t("SIGNUP_SECTION_ADDRESS") },
    ];

    const goToStep = (target: 1 | 2 | 3 | 4) => {
        setStep(target);
        setActiveField(null);
        setBlockedReason(null);
        /* Arriving at a step should never start with red fields: errors left
           over from an earlier attempt (or from a step you already passed)
           belong to that moment, not this one. Next re-checks the step you
           are leaving, and Sign Up re-runs validateForm, so nothing escapes. */
        setErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNext = () => {
        const current = step as 1 | 2 | 3;
        const stepErrors = stepFieldErrors(current);

        if (Object.keys(stepErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...stepErrors }));
            /* the first problem is the most actionable one to say out loud */
            setBlockedReason(Object.values(stepErrors)[0]);
            if (!isWideScreen) toast.error(t("PLEASE_FILL_ALL_REQUIRED_FIELDS_CORRECTLY") as string);
            return;
        }

        setBlockedReason(null);
        goToStep((current + 1) as 2 | 3 | 4);
    };

    /* No beforeunload guard: the in-form notice already tells the user that
       nothing is saved, and the browser prompt was intrusive. */


    const handleInputChange = (field: keyof RegistrationFormData, value: string | boolean) => {
        setBlockedReason(null);
        setAnnouncement(null);
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            if (field === 'noIdNumber') {
                if (value) {
                    newData.socialSecurityNumber = '';
                } else {
                    newData.dateOfBirth = '';
                }
            }

            return newData;
        });

        const newErrors = { ...errors };
        if (field === 'firstName' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.firstName = '';
            }
            else if (!validateLatinCharacters(trimmedValue)) {
               newErrors.firstName = "FIRST_NAME_LATIN_ONLY";
            }
            else {
                newErrors.firstName = '';
            }
        }
        if (field === 'lastName' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.lastName = '';
            }
            else if (!validateLatinCharacters(trimmedValue)) {
               newErrors.lastName = "LAST_NAME_LATIN_ONLY";
            }
            else {
                newErrors.lastName = '';
            }
        }

        if (field === 'email' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty (avoid red highlight)
            if (!trimmedValue) {
                newErrors.email = '';
            }
            // Show error only when user entered an invalid email
            else if (!validateEmail(trimmedValue)) {
                newErrors.email = "EMAIL_INVALID";
            }
            else {
                newErrors.email = '';
            }
        }

        if (field === 'password' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty
            if (!trimmedValue) {
                newErrors.password = '';
            }
            // Error only when user entered an invalid password
            else if (!validatePassword(trimmedValue)) {
                newErrors.password = "PASSWORD_RULE";
            }
            else {
                newErrors.password = '';
            }
        }

        if (field === 'zipCode' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty
            if (!trimmedValue) {
                newErrors.zipCode = '';
            }
            // Error only if user typed invalid postal code
            else if (!validatePostalCode(trimmedValue)) {
                newErrors.zipCode = "POSTAL_CODE_INVALID";
            }
            else {
                newErrors.zipCode = '';
            }
        }

        if (field === 'phoneNo' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.phoneNo = '';
            }
            else if (!validatePhoneNumber(trimmedValue)) {
                newErrors.phoneNo = "INVALID_PHONE_NUMBER";
            }
            else {
                newErrors.phoneNo = '';
            }
        }

        // City / profession are optional — only flag characters we cannot store
        if ((field === 'city' || field === 'profession') && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue || validateLatinCharacters(trimmedValue)) {
                newErrors[field] = '';
            } else {
                newErrors[field] = field === 'city' ? "CITY_LATIN_ONLY" : "PROFESSION_LATIN_ONLY";
            }
        }

        if (field === 'agreeToTerms' && typeof value === 'boolean') {
            if (!value) {
                newErrors.agreeToTerms = "PLEASE_SELECT_TERMS_AND_CONDITION";
            } else {
                newErrors.agreeToTerms = '';
            }
        }

        if (field === 'noIdNumber') {
            newErrors.socialSecurityNumber = '';
            newErrors.dateOfBirth = '';
        }

        /* These four have no per-keystroke rule of their own: SSN and date of
           birth explain themselves through their live checklist, and address /
           country are simple presence checks. Without this, an error raised by
           Next stayed on screen (and in the guide's mouth) even after the user
           had corrected the value. Next re-validates on the way out anyway. */
        if (['socialSecurityNumber', 'dateOfBirth', 'address', 'country'].includes(field as string)) {
            newErrors[field as string] = '';
        }

        setErrors(newErrors);
    };

      async function hashEmail(email: string) {
        const hash = CryptoJS.SHA256(email.trim().toLowerCase());
        return hash.toString(CryptoJS.enc.Hex);
      }
    
      async function pushHashedEmail(email: string) {
        const isProduction = import.meta.env.VITE_NODE_ENV === "production";
        if (isProduction) {
          const hashedEmail = await hashEmail(email);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "create_account",
            email: hashedEmail,
          });
        }
      }
    const onSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        /* ignore a submit that wasn't a real click on the final step */
        if (step !== 4 || !submitArmed) return;

        /* capture the errors as validateForm reports them, so the character can
           name the first problem — one call, same setErrors as before */
        const collected: FormErrors = {};
        const isValid = validateForm(formData, t, (formErrors) => {
            Object.assign(collected, formErrors);
            setErrors(formErrors);
        });

        if (!isValid) {
            const firstProblem = Object.values(collected)[0] || 'PLEASE_FILL_ALL_REQUIRED_FIELDS_CORRECTLY';
            if (isWideScreen) {
                setBlockedReason(firstProblem);
                /* the character and the toast say the same thing */
                rawToast.error(t(firstProblem) as string);
            } else {
                toast.error(t("PLEASE_FILL_ALL_REQUIRED_FIELDS_CORRECTLY") as string);
            }
            return;
        }

        try {
            setLoading(true);
            const registrationPayload = prepareRegistrationPayload(formData, accountType, currentLanguage , tduid);
            const resolvedReferredByCode = referredByCode || getItem('inviteCode') || '';
            const resolvedReferredByUserId = referredByUserId || getItem('userId') || '';

            if (resolvedReferredByCode) {
                registrationPayload.ReferredByCode = resolvedReferredByCode;
                registrationPayload.ReferredByUserId = resolvedReferredByUserId;
            }
            if(isFreelancerInvitated){
                registrationPayload.invitedBy = Number(resolvedReferredByUserId);
            }
            const response = await dispatch(registerUser(registrationPayload));

            if (response.type === 'signup/registerUser/fulfilled') {
                notify(t("REGISTRATION_SUCCESSFUL") as string, 'success');
                const resp = await dispatch(signInWithEmailAndPassword({
                    email: formData.email,
                    password: formData.password,
                }));
                if (resp.type === 'signin/signInWithEmailAndPassword/fulfilled') {
                    window.dataLayer = window.dataLayer || [];
                    await pushHashedEmail(formData.email);
                    navigate('/v2/dashboard?lang=' + currentLanguage);
                    return;
                }
                if (resp.type === 'signin/signInWithEmailAndPassword/rejected') {
                    const errorMessage = resp.payload ?? t("SIGNIN_FAILED") as string;
                    notifySubmit(t(errorMessage) as string);
                }
            } else {
                const errorMessage = response.payload || submitError || t("Registration failed. Please try again.") as string;
                notifySubmit(t(errorMessage) as string);
            }
        } catch (error) {
            notifySubmit(t("REGISTRATION_ERROR") as string);
            console.error("Registration error:", error);
        } finally {
            setLoading(false);
        }
    }, [formData, validateForm, accountType, currentLanguage, dispatch, submitError, t, step, submitArmed, isWideScreen, notify, notifySubmit, rawToast]);

    const handleSocialLogin = (provider: string) => {
        if (provider === 'bankid') {
            setSignupMode('bankid-active');
        }
    };

    const handleSSNInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = handleSSNInput(e.target.value);
        handleInputChange('socialSecurityNumber', value);

        // Stripping a letter leaves the stored value unchanged, and React then skips the
        // re-render — the box would keep the letter on screen. Put the cleaned value back.
        if (e.target.value !== value) {
            e.target.value = value;
        }
    };

    // Only the digits are kept (plus the "000" pad handleDOBInput adds); the slashes are put
    // back by formatDOBDisplay when rendering, which is what makes the field self-format.
    const handleDOBInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
        const value = handleDOBInput(digits);
        handleInputChange('dateOfBirth', value);

        // Anything the user typed that is not part of the format is dropped above. When that
        // leaves the stored value unchanged React skips the re-render, so the box would keep
        // the stray characters — write the formatted text back to keep them in step.
        const display = formatDOBDisplay(value);
        if (e.target.value !== display) {
            e.target.value = display;
        }
    };

    const handleDOBInputFocus = () => {
        setShowDOBCalendar(true);
    };

    // A pure function of the value in the box — nothing is read from errors state for this
    // field. That is deliberate: a stored message outlives the value it was written for (an
    // edit, a submit, a hot reload keeps it) and would then contradict a perfectly valid
    // date. A wrong year/month/day is reported as soon as that part is complete; "the date is
    // incomplete" only matters once the user has tried to submit.
    const dobValidation = validateDOB(formData.dateOfBirth);
    const dateOfBirthError = dobValidation === 'DOB_MUST_HAVE_8_DIGITS'
        ? (submitArmed ? dobValidation : '')
        : dobValidation;
    // Fed to the message so it can quote the month, the day and how many days that month has.
    const dobDigits = formData.dateOfBirth.replace(/\D/g, '');
    const dobMessageValues = {
        year: dobDigits.slice(0, 4),
        month: dobDigits.slice(4, 6),
        day: dobDigits.slice(6, 8),
        days: dobDaysInMonth(formData.dateOfBirth),
    };


    return (
        <div className="registration-page-wrapper auth-elevate">
            <div className="auth-blobs" aria-hidden="true">
                <span className="auth-blob auth-blob--one" />
                <span className="auth-blob auth-blob--two" />
            </div>
            <RegistrationHeader />
            <Container>
                <Row>
                    {/* Desktop only — see the note on the sign-in page: below
                        992px the wizard owns the screen and messages go to
                        toasts, so the character has nothing left to do. */}
                    {isWideScreen && (
                    <Col xs={12} lg={5} className="px-0 auth-hero-col">
                        <div className="auth-hero">
                            <AuthHeroTitle title={String(t("REGISTRATION_TITLE"))} />
                            <SignupGuide context={guideContext} layout="panel" />
                        </div>
                    </Col>
                    )}

                    <Col xs={12} lg={7} className="px-0 auth-form-col">
                        <div
                            ref={cardRef}
                            className={`auth-card${signupMode === 'email' ? ' auth-card--scroll' : ''}`}
                        >
                            {signupMode === 'bankid-active' && (
                                <div className="bankid-signup-wrapper">
                                    <p className="bankid-signup-subtitle">{t("BANKID_SIGNUP_SUBTITLE")}</p>
                                    <div ref={bankidWrapRef} {...bankidHoverProps}>
                                        <BankIDSignupInline accountType={accountType} onSuccess={() => {}} />
                                    </div>
                                    <div className="sign-up-section" style={{ marginTop: '16px' }}>
                                        <span>{t("REGISTRATION_ALREADY_HAVE_ACCOUNT")}</span>
                                        <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postinloggning', 'en-us': 'emaillogin' })} className="sign-up-link" onMouseEnter={() => setActiveField('signinLink')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('signinLink')} onBlur={() => setActiveField(null)}>{t("REGISTRATION_SIGN_IN")}</Link>
                                    </div>

                                    <div className="signin-or-divider">
                                        <span className="signin-or-line" />
                                        <span className="signin-or-text">or</span>
                                        <span className="signin-or-line" />
                                    </div>
                                    <p className="signin-bankid-hint">
                                        {t("SIGNUP_BANKID_HINT")}
                                    </p>
                                    <button
                                        type="button"
                                        className="signin-email-fallback-btn"
                                        onClick={() => setSignupMode('email')}
                                        onMouseEnter={() => setActiveField('switchEmail')} onMouseLeave={() => setActiveField(null)} onFocus={() => setActiveField('switchEmail')} onBlur={() => setActiveField(null)}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{t("SIGN_UP_WITH_EMAIL")}</span>
                                    </button>
                                </div>
                            )}

                            {signupMode === 'email' && (
                            <div className="registration-form-container">
                                {/* sticky head: the three labelled steps */}
                                <div className="auth-card__head">
                                    <ol className="auth-stepper" aria-label={String(t("SIGNUP_STEP_COUNTER", { current: step, total: 4 }))}>
                                        {steps.map(({ id, label }) => {
                                            const state = id < step ? 'is-done' : id === step ? 'is-active' : 'is-todo';

                                            return (
                                                <li key={id} className={`auth-stepper__step ${state}`} aria-current={id === step ? 'step' : undefined}>
                                                    <button
                                                        type="button"
                                                        className="auth-stepper__hit"
                                                        onClick={() => id < step && goToStep(id)}
                                                        disabled={id >= step}
                                                    >
                                                        <span className="auth-stepper__dot">
                                                            {id < step ? (
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                                    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            ) : id}
                                                        </span>
                                                        <span className="auth-stepper__label">{label}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ol>

                                    <div className={`auth-scroll-hint auth-scroll-hint--up${scrollNav.up ? ' is-visible' : ''}`}>
                                        <button
                                            type="button"
                                            className="auth-scroll-hint__pill"
                                            onClick={() => scrollCardByHint(-1)}
                                            tabIndex={scrollNav.up ? 0 : -1}
                                            aria-label={String(t('PARTNER_SWIPE_UP'))}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            {t('PARTNER_SWIPE_UP')}
                                        </button>
                                    </div>
                                </div>

                                <form className="registration-form" onSubmit={onSubmit}>
                                    {step === 1 && (
                                    <fieldset className="auth-section">
                                        <legend className="auth-section__legend">
                                            <span className="auth-section__num" aria-hidden="true">1</span>
                                            {t("SIGNUP_SECTION_ACCOUNT_TYPE")}
                                        </legend>
                                        <div className="auth-section__body">
                                        <div className="account-type-section">
                                            <div className="account-type-label">
                                                {t("REGISTRATION_ACCOUNT_TYPE_QUESTION")}
                                            </div>
                                            <p className="account-type-help">
                                                {t("GUIDE_ACCOUNT_TYPE")}
                                            </p>
                                            <div
                                                onMouseEnter={() => setActiveField('accountType')}
                                                onFocus={() => setActiveField('accountType')}
                                                onMouseLeave={() => setActiveField(null)}
                                                onBlur={() => setActiveField(null)}
                                            >
                                            <PillTabs
                                                fullWidth
                                                rich
                                                ariaLabel={String(t("REGISTRATION_ACCOUNT_TYPE_QUESTION"))}
                                                activeKey={accountType}
                                                tabs={accountTypes.map(({ key, icon, label, description }) => ({
                                                    key,
                                                    label: String(label),
                                                    description: String(description),
                                                    icon: <img src={icon} alt="" />,
                                                }))}
                                                onChange={(key) => {
                                                    setAccountType(key as 'freelancer' | 'business');
                                                    handleInputChange('accountType', key);
                                                }}
                                            />
                                            </div>

                                            {/* What the remaining steps ask for. The client's worry was
                                                people abandoning a form that "feels too long" — saying
                                                up front what is left answers that better than a promise
                                                about how long it takes. Each line mirrors the fields
                                                those steps actually render. */}
                                            <div className="account-type-next">
                                                <span className="account-type-next__title">
                                                    {t("SIGNUP_NEXT_TITLE")}
                                                </span>
                                                <ul className="account-type-next__list">
                                                    {[
                                                        { id: 2, label: t("SIGNUP_SECTION_ACCOUNT"), hint: t("SIGNUP_NEXT_ACCOUNT") },
                                                        { id: 3, label: t("SIGNUP_SECTION_PERSONAL"), hint: t("SIGNUP_NEXT_PERSONAL") },
                                                        { id: 4, label: t("SIGNUP_SECTION_ADDRESS"), hint: t("SIGNUP_NEXT_ADDRESS") },
                                                    ].map(({ id, label, hint }) => (
                                                        <li key={id} className="account-type-next__item">
                                                            <span className="account-type-next__num" aria-hidden="true">{id}</span>
                                                            <span className="account-type-next__text">
                                                                <span className="account-type-next__step">{label}</span>
                                                                <span className="account-type-next__hint">{hint}</span>
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                        </div>
                                    </fieldset>
                                    )}

                                    {step === 2 && (
                                    <fieldset className="auth-section">
                                        <legend className="auth-section__legend">
                                            <span className="auth-section__num" aria-hidden="true">2</span>
                                            {t("SIGNUP_SECTION_ACCOUNT")}
                                        </legend>
                                        <div className="auth-section__body">
                                    <div className="auth-field-row">
                                        <UIInput
                                            label={t("REGISTRATION_FIRST_NAME_LABEL")}
                                            placeholder={t("REGISTRATION_FIRST_NAME_PLACEHOLDER")}
                                            value={formData.firstName}
                                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        onFocus={() => setActiveField('firstName')}
                                        onBlur={() => setActiveField(null)}
                                            error={!!errors.firstName}
                                            errorMessage={errors.firstName ? t(errors.firstName) : ''}
                                            leadingIcon={<UserFieldIcon />}
                                            valid={isFieldValid.firstName}
                                        />
                                        <UIInput
                                            label={t("REGISTRATION_LAST_NAME_LABEL")}
                                            placeholder={t("REGISTRATION_LAST_NAME_PLACEHOLDER")}
                                            value={formData.lastName}
                                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        onFocus={() => setActiveField('lastName')}
                                        onBlur={() => setActiveField(null)}
                                            error={!!errors.lastName}
                                            errorMessage={errors.lastName ? t(errors.lastName) : ''}
                                            leadingIcon={<UserFieldIcon />}
                                            valid={isFieldValid.lastName}
                                        />
                                    </div>

                                    {/* Email Field */}
                                    <UIInput
                                        type="email"
                                        label={t("REGISTRATION_EMAIL_LABEL")}
                                        placeholder={t("REGISTRATION_EMAIL_PLACEHOLDER")}
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        onFocus={() => setActiveField('email')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.email}
                                        errorMessage={errors.email ? t(errors.email) : ''}
                                        leadingIcon={<MailFieldIcon />}
                                        valid={isFieldValid.email}
                                    />

                                    {/* Password Field */}
                                    <UIInput
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        label={t("REGISTRATION_PASSWORD_LABEL")}
                                        placeholder={t("REGISTRATION_PASSWORD_PLACEHOLDER")}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        onFocus={() => setActiveField('password')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.password}
                                        errorMessage={errors.password ? t(errors.password) : ''}
                                        onIconClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                        leadingIcon={<LockFieldIcon />}
                                        icon={
                                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                <path d="M2.92442 10.9778C2.63978 10.3585 2.63978 9.6441 2.92442 9.02484C4.242 6.15832 7.13866 4.16797 10.5002 4.16797C13.8617 4.16797 16.7584 6.15833 18.076 9.02484C18.3606 9.6441 18.3606 10.3585 18.076 10.9778C16.7584 13.8443 13.8617 15.8346 10.5002 15.8346C7.13866 15.8346 4.24201 13.8443 2.92442 10.9778Z" stroke="black" strokeWidth="2" />
                                                <path d="M13.0002 10.0013C13.0002 11.382 11.8809 12.5013 10.5002 12.5013C9.11948 12.5013 8.00019 11.382 8.00019 10.0013C8.00019 8.62059 9.11948 7.5013 10.5002 7.5013C11.8809 7.5013 13.0002 8.62059 13.0002 10.0013Z" stroke="black" strokeWidth="2" />
                                            </svg>
                                        }
                                    />

                                    <PasswordStrength value={formData.password} />
                                        </div>
                                    </fieldset>
                                    )}

                                    {step === 3 && (
                                    <fieldset className="auth-section">
                                        <legend className="auth-section__legend">
                                            <span className="auth-section__num" aria-hidden="true">3</span>
                                            {t("SIGNUP_SECTION_PERSONAL")}
                                        </legend>
                                        <div className="auth-section__body">
                                    {/* Social Security Number Field */}
                                    {!formData.noIdNumber && (
                                        <div className="auth-field-group">
                                            <UIInput
                                                type="text"
                                                label={t("REGISTRATION_SSN_LABEL")}
                                                placeholder={t("REGISTRATION_SSN_PLACEHOLDER")}
                                                value={formData.socialSecurityNumber}
                                                onChange={handleSSNInputChange}
                                        onFocus={() => setActiveField('socialSecurityNumber')}
                                        onBlur={() => setActiveField(null)}
                                                inputMode="numeric"
                                            maxLength={12}
                                            error={!!errors.socialSecurityNumber}
                                                errorMessage={errors.socialSecurityNumber ? t(errors.socialSecurityNumber) : ''}
                                                leadingIcon={<IdFieldIcon />}
                                                valid={isFieldValid.socialSecurityNumber}
                                                pattern="[0-9]*"
                                            />
                                            <p className="auth-field-hint">{t("REGISTRATION_SSN_HINT")}</p>
                                            <FieldRules rules={ssnRules} show={!!ssnValue} />
                                        </div>
                                    )}
                                    {/* No ID Number Checkbox */}
                                    <div className="checkbox-wrapper">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="checkbox-input"
                                                checked={formData.noIdNumber}
                                                onChange={(e) => handleInputChange('noIdNumber', e.target.checked)}
                                            />
                                            <span className="checkbox-checkmark"></span>
                                            <span className="checkbox-text">{t("REGISTRATION_NO_ID_NUMBER")}</span>
                                        </label>
                                    </div>
                                    {/* Date of Birth Field */}
                                    {formData?.noIdNumber && (
                                        <>
                                            <div className="auth-field-group">
                                            <UIInput
                                                type="text"
                                                label={t("ENTER_DATE_OF_BIRTH")}
                                                placeholder="YYYY/MM/DD"
                                                value={formatDOBDisplay(formData.dateOfBirth)}
                                                onChange={handleDOBInputChange}
                                                onFocus={() => { handleDOBInputFocus(); setActiveField('dateOfBirth'); }}
                                                onBlur={() => setActiveField(null)}
                                                inputMode="numeric"
                                                maxLength={10}
                                                error={!!dateOfBirthError}
                                                errorMessage={dateOfBirthError ? t(dateOfBirthError, dobMessageValues) : ''}
                                                leadingIcon={<CalendarFieldIcon />}
                                                valid={isFieldValid.dateOfBirth}
                                                icon={
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={17}
                                                        height={19}
                                                        viewBox="0 0 17 19"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z"
                                                            stroke="black"
                                                            strokeWidth={2}
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>

                                                }
                                            />
                                            <FieldRules rules={dobRules} show={!!formData.dateOfBirth} />
                                            </div>

                                            {/* DOB Calendar Modal */}
                                            {showDOBCalendar && (
                                                <div className="calendar-modal-overlay" ref={dropdownRef} onClick={() => setShowDOBCalendar(false)}>
                                                    <div className="calendar-modal-content" onClick={(e) => e.stopPropagation()}>
                                                        <div className="calendar-modal-header">
                                                            <h3>{t("SELECT_DATE_OF_BIRTH") || "Select Date of Birth"}</h3>
                                                            <button
                                                                type="button"
                                                                className="calendar-modal-close"
                                                                onClick={() => setShowDOBCalendar(false)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                        <CalendarSingle
                                                            onDateSelect={(date) => {
                                                                const year = date.getFullYear();
                                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                const day = String(date.getDate()).padStart(2, '0');
                                                                const formattedDate = `${year}${month}${day}000`;
                                                                handleInputChange('dateOfBirth', formattedDate);
                                                                setShowDOBCalendar(false);
                                                            }}
                                                            label="Date of Birth"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {/* Profession */}
                                    <UIInput
                                        type="text"
                                        label={t("PROFESSION")}
                                        placeholder={t("PROFESSION")}
                                        value={formData.profession}
                                        onChange={(e) => handleInputChange('profession', e.target.value)}
                                        onFocus={() => setActiveField('profession')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.profession}
                                        errorMessage={errors.profession ? t(errors.profession) : ''}
                                        leadingIcon={<ProfessionFieldIcon />}
                                        valid={isFieldValid.profession}
                                    />

                                    {/* Phone Number */}
                                    <UIInput
                                        type="text"
                                        label={t("PHONE_NUMBER_LABEL")}
                                        placeholder={t("PHONE_NUMBER_LABEL")}
                                        value={formData.phoneNo}
                                        /* `inputMode`/`pattern` only hint the on-screen keyboard —
                                           a desktop keyboard types letters straight through them.
                                           handlePhoneInput() strips everything but digits (and caps
                                           at 13, the same limit validatePhoneNumber enforces), the
                                           way the SSN field already does. */
                                        onChange={(e) => handleInputChange('phoneNo', handlePhoneInput(e.target.value))}
                                        onFocus={() => setActiveField('phoneNo')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.phoneNo}
                                        errorMessage={errors.phoneNo ? t(errors.phoneNo) : ''}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={13}
                                        leadingIcon={<PhoneFieldIcon />}
                                        valid={isFieldValid.phoneNo}
                                    />

                                        </div>
                                    </fieldset>
                                    )}

                                    {step === 4 && (
                                    <fieldset className="auth-section">
                                        <legend className="auth-section__legend">
                                            <span className="auth-section__num" aria-hidden="true">4</span>
                                            {t("SIGNUP_SECTION_ADDRESS")}
                                        </legend>
                                        <div className="auth-section__body">
                                    {/* Address */}
                                    <UIInput
                                        type="text"
                                        label={t("ADDRESS_LABEL")}
                                        placeholder={t("ADDRESS_LABEL")}
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        onFocus={() => setActiveField('address')}
                                        onBlur={() => setActiveField(null)}
                                        error={!!errors.address}
                                        errorMessage={errors.address ? t(errors.address) : ''}
                                        leadingIcon={<AddressFieldIcon />}
                                        valid={isFieldValid.address}
                                    />

                                    {/* Postal code + City */}
                                    <div className="auth-field-row">
                                        <UIInput
                                            type="text"
                                            label={t("POSTAL_CODE_LABEL")}
                                            placeholder={t("POSTAL_CODE_LABEL")}
                                            maxLength={10}
                                            onFocus={() => setActiveField('zipCode')}
                                            onBlur={() => setActiveField(null)}
                                            value={formData.zipCode}
                                            onChange={(e) => {
                                                const sanitizedValue = e.target.value.replace(/[^A-Za-z0-9\s,-]/g, '');
                                                handleInputChange('zipCode', sanitizedValue);
                                            }}
                                            error={!!errors.zipCode}
                                            errorMessage={errors.zipCode ? t(errors.zipCode) : ''}
                                            leadingIcon={<PostalFieldIcon />}
                                            valid={isFieldValid.zipCode}
                                        />
                                        <UIInput
                                            type="text"
                                            label={t("CITY")}
                                            placeholder={t("CITY")}
                                            value={formData.city}
                                            onChange={(e) => handleInputChange('city', e.target.value)}
                                        onFocus={() => setActiveField('city')}
                                        onBlur={() => setActiveField(null)}
                                            error={!!errors.city}
                                            errorMessage={errors.city ? t(errors.city) : ''}
                                            leadingIcon={<CityFieldIcon />}
                                            valid={isFieldValid.city}
                                        />
                                    </div>

                                      <div className="form-group">
                                        <label className="form-label" htmlFor="country-select">{t('COUNTRY')}</label>
                                        <div
                                            onFocus={() => setActiveField('country')}
                                            onBlur={() => setActiveField(null)}
                                            onMouseEnter={() => setActiveField('country')}
                                            onMouseLeave={() => setActiveField(null)}
                                        >
                                        <SelectField
                                            id="country-select"
                                            searchable
                                            options={countryOptions}
                                            value={formData.country}
                                            onChange={(country) => handleInputChange('country', country)}
                                            placeholder={String(t('SELECT_YOUR_COUNTRY'))}
                                            disabled={loading}
                                            valid={isFieldValid.country}
                                            leadingIcon={<GlobeFieldIcon />}
                                            ariaLabel={String(t('COUNTRY'))}
                                        />
                                        </div>
                                    </div>
                                        </div>
                                    </fieldset>
                                    )}

                                    {step === 4 && (
                                    <>
                                    <div
                                        className="checkbox-wrapper auth-terms-box"
                                        onMouseEnter={() => setActiveField('agreeToTerms')}
                                        onMouseLeave={() => setActiveField(null)}
                                    >
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="checkbox-input"
                                                checked={formData.agreeToTerms}
                                                onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                                            />
                                            <span className="checkbox-checkmark"></span>
                                            <span className="checkbox-text">
                                                {t("REGISTRATION_TERMS_TEXT_1")}

                                                <Link
                                                    to={generalConditionsUrl}
                                                    target="_blank"
                                                >
                                                    {t("REGISTRATION_TERMS_LINK_1")}
                                                </Link>
                                                {t("REGISTRATION_TERMS_TEXT_2")}
                                                <Link
                                                    to={currentLanguage === LOCALES.EN_US
                                                        ? invoicePDF
                                                        : invoiceSwidishPDF
                                                    }
                                                    target="_blank"
                                                >
                                                    {t("REGISTRATION_TERMS_LINK_2")}</Link>
                                                {t("REGISTRATION_TERMS_TEXT_3")}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Display Form Errors */}
                                    {errors.agreeToTerms && (
                                        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                            {t(errors.agreeToTerms)}
                                        </div>
                                    )}

                                    {/* Display Redux Error */}
                                    {submitError && (
                                        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                            {t(submitError)}
                                        </div>
                                    )}
                                    </>
                                    )}


                                    <div className="auth-card__foot">
                                    <div className="auth-foot-alt">
                                        <span className="auth-foot-alt__rule" aria-hidden="true" />
                                        <button
                                            type="button"
                                            className="auth-foot-alt__bankid"
                                            onClick={() => setSignupMode('bankid-active')}
                                        >
                                            <img src={bankidcolor} alt="" width={15} height={15} style={{ objectFit: 'contain' }} />
                                            <span>{t("SIGNUP_WITH_BANKID")}</span>
                                        </button>
                                        <span className="auth-foot-alt__rule" aria-hidden="true" />
                                    </div>

                                    <div className="auth-foot-bar">
                                    <div className="auth-step-nav">
                                        {step > 1 && (
                                            <button
                                                type="button"
                                                className="auth-step-back"
                                                onClick={() => goToStep((step - 1) as 1 | 2 | 3)}
                                                disabled={loading || isSubmitting}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M19 12H6M11 5.5L5.5 12l5.5 6.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <span>{t("BACK")}</span>
                                            </button>
                                        )}

                                        {step < 4 ? (
                                            <button key="wizard-next" type="button" className="submit-btn" onClick={handleNext}>
                                                <span>{t("NEXT")}</span>
                                                <CtaArrowIcon />
                                            </button>
                                        ) : (
                                            <button
                                                key="wizard-submit"
                                                type="submit"
                                                className="submit-btn"
                                                disabled={loading || isSubmitting}
                                                onMouseEnter={() => setActiveField('submit')}
                                                onMouseLeave={() => setActiveField(null)}
                                                onFocus={() => setActiveField('submit')}
                                                onBlur={() => setActiveField(null)}
                                            >
                                                <span>{(loading || isSubmitting) ? t("REGISTRATION_SUBMITTING") : t("REGISTRATION_SUBMIT")}</span>
                                                {!(loading || isSubmitting) && <CtaArrowIcon />}
                                            </button>
                                        )}
                                    </div>

                                    <div className="auth-foot-links">
                                        <span>
                                            {t("REGISTRATION_ALREADY_HAVE_ACCOUNT")}
                                            <Link
                                                to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postinloggning', 'en-us': 'emaillogin' })}
                                                className="sign-up-link"
                                            >
                                                {t("REGISTRATION_SIGN_IN")}
                                            </Link>
                                        </span>
                                    </div>
                                    </div>

                                    <div className={`auth-scroll-hint auth-scroll-hint--down${scrollNav.down ? ' is-visible' : ''}`}>
                                        <button
                                            type="button"
                                            className="auth-scroll-hint__pill"
                                            onClick={() => scrollCardByHint(1)}
                                            tabIndex={scrollNav.down ? 0 : -1}
                                            aria-label={String(t('PARTNER_SWIPE_DOWN'))}
                                        >
                                            {t('PARTNER_SWIPE_DOWN')}
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    </div>
                                </form>
                            </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container >

        </div >
    )
}


export default Registration
